import React, { useState, useCallback } from 'react';
import { Button, CircularProgress, Box, Typography } from '@mui/material';
import CreditCardIcon from '@mui/icons-material/CreditCard';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import client from '../API';

const StripeConnectButton = ({
  onError,
  isCreator = false,
  variant = 'contained',
  fullWidth = false,
  disabled = false,
  redirectPath = null,
  size = 'medium',
}) => {
  const [loading, setLoading] = useState(false);

  const handleConnect = useCallback(async () => {
    try {
      setLoading(true);

      // Use appropriate API based on user type
      const api = isCreator
        ? client.creatorConnect.stripeOAuth
        : client.userWallets.stripeOAuth;

      // Use provided redirectPath or default based on user type
      const path = redirectPath || (isCreator ? '/creatorconnect/dashboard' : '/dashboard?tab=account');

      const response = await api.initiate(path);

      if (response.authorization_url) {
        // Redirect to Stripe for authentication
        window.location.href = response.authorization_url;
      } else {
        throw new Error('No authorization URL received');
      }
    } catch (error) {
      setLoading(false);
      console.error('Stripe OAuth error:', error);

      // Handle specific error cases
      if (error.response?.status === 409) {
        onError?.(new Error('Stripe account already connected'));
      } else {
        onError?.(error);
      }
    }
  }, [isCreator, redirectPath, onError]);

  return (
    <Button
      variant={variant}
      fullWidth={fullWidth}
      disabled={disabled || loading}
      onClick={handleConnect}
      size={size}
      startIcon={
        loading ? (
          <CircularProgress size={20} color="inherit" />
        ) : (
          <CreditCardIcon />
        )
      }
      sx={{
        backgroundColor: '#635bff',
        '&:hover': {
          backgroundColor: '#4f46e5',
        },
        color: 'white',
        textTransform: 'none',
        fontWeight: 600,
        py: 1.5,
        px: 3,
        borderRadius: 2,
      }}
    >
      {loading ? 'Connecting...' : 'Connect with Stripe'}
    </Button>
  );
};

export const StripeVerifiedBadge = ({ accountId, status }) => {
  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1,
        p: 2,
        bgcolor: 'success.light',
        borderRadius: 2,
        border: '1px solid',
        borderColor: 'success.main',
      }}
    >
      <CheckCircleIcon color="success" />
      <Box>
        <Typography variant="body1" fontWeight={600} color="success.dark">
          Stripe Connected
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Account: {accountId || 'Connected'}
          {status && ` (${status})`}
        </Typography>
      </Box>
    </Box>
  );
};

export default StripeConnectButton;
